import {FC} from 'react'
import {Link} from 'react-router-dom'
import clsx from 'clsx'
import {formatInvoiceCount} from '../../../modules/apps/invoice-management/invoices-list/core/_formatters'
import {getInvoiceEntrySourceInfo} from '../../../modules/apps/invoice-management/invoices-list/core/invoiceEntrySourceHelpers'
import {DashboardLiveOperation, DashboardLiveStream, DashboardSendQueue} from './core/_models'
import {readDashboardError} from './core/_requests'
import {
  formatAge,
  formatDayLabel,
  getOperationIcon,
  getOperationProgressText,
  getOperationStateLabel,
  getOperationTitle,
  getOperationUrl,
  getSendQueueListUrl,
} from './core/dashboardActivityPresentation'
import {isLiveSnapshotBusy, useDashboardLive} from './core/useDashboardData'

type Props = {
  companyId: number
}

const SendQueueTile: FC<{sending: DashboardSendQueue}> = ({sending}) => {
  const pending = Number(sending.pending || 0)
  const unknown = Number(sending.unknown || 0)

  return (
    <article className={clsx('mp-live-tile', pending > 0 ? 'is-active' : 'is-quiet')}>
      <div className='mp-live-tile__head'>
        <span className='mp-live-tile__icon is-blue'>
          <i className='las la-paper-plane' aria-hidden='true' />
        </span>
        <span className='mp-live-tile__title'>ارسال به سامانه مؤدیان</span>
      </div>
      {pending > 0 ? (
        <Link
          className='mp-live-tile__figure'
          to={getSendQueueListUrl('pending')}
          aria-label={`${formatInvoiceCount(
            pending
          )} صورتحساب در صف ارسال یا استعلام؛ مشاهده در فهرست`}
        >
          <strong>{formatInvoiceCount(pending)}</strong>
          <span>در صف ارسال یا استعلام</span>
        </Link>
      ) : (
        <p className='mp-live-tile__calm'>
          <i className='las la-check-circle' aria-hidden='true' />
          صف ارسال خالی است.
        </p>
      )}
      <ul className='mp-live-tile__facts'>
        {pending > 0 &&
          sending.oldestPendingAgeSeconds !== null &&
          sending.oldestPendingAgeSeconds !== undefined && (
            <li>قدیمی‌ترین مورد صف: ثبت‌شده {formatAge(sending.oldestPendingAgeSeconds)}</li>
          )}
        {unknown > 0 && (
          <li>
            <Link to={getSendQueueListUrl('unknown')}>
              {formatInvoiceCount(unknown)} صورتحساب با استعلام نامشخص
            </Link>
          </li>
        )}
      </ul>
    </article>
  )
}

// Nothing but the send queue to report: one line, where a lone tile used to stand in an
// otherwise empty row.
const SendQueueStrip: FC<{sending: DashboardSendQueue}> = ({sending}) => {
  const pending = Number(sending.pending || 0)
  const unknown = Number(sending.unknown || 0)
  const oldest = sending.oldestPendingAgeSeconds

  return (
    <div className={clsx('mp-live-strip', pending > 0 && 'is-active')}>
      <span className={clsx('mp-live-tile__icon', pending > 0 ? 'is-blue' : 'is-green')}>
        <i
          className={clsx('las', pending > 0 ? 'la-paper-plane' : 'la-check-circle')}
          aria-hidden='true'
        />
      </span>
      <div className='mp-live-strip__body'>
        {pending > 0 ? (
          <Link
            className='mp-live-strip__title'
            to={getSendQueueListUrl('pending')}
            aria-label={`${formatInvoiceCount(
              pending
            )} صورتحساب در صف ارسال یا استعلام؛ مشاهده در فهرست`}
          >
            <strong>{formatInvoiceCount(pending)}</strong> صورتحساب در صف ارسال یا استعلام
          </Link>
        ) : (
          <span className='mp-live-strip__title'>صف ارسال به سامانه مؤدیان خالی است.</span>
        )}
        <span className='mp-live-strip__note'>
          {pending > 0 && oldest !== null && oldest !== undefined
            ? `قدیمی‌ترین مورد صف: ثبت‌شده ${formatAge(oldest)}. `
            : ''}
          در حال حاضر عملیات گروهی یا دریافتی در جریان نیست. با شروع ورود اکسل، ارسال گروهی یا
          دریافت از وب‌سرویس، پیشرفت آن همین‌جا نمایش داده می‌شود.
        </span>
      </div>
      {unknown > 0 && (
        <Link className='mp-live-strip__aside' to={getSendQueueListUrl('unknown')}>
          {formatInvoiceCount(unknown)} صورتحساب با استعلام نامشخص
          <i className='las la-angle-left' aria-hidden='true' />
        </Link>
      )}
    </div>
  )
}

const StreamTile: FC<{stream: DashboardLiveStream; minutes: number}> = ({stream, minutes}) => {
  const source = getInvoiceEntrySourceInfo(stream.entryType)
  return (
    <article className='mp-live-tile is-active'>
      <div className='mp-live-tile__head'>
        <span className='mp-live-tile__icon is-green'>
          <i className={`las ${source.icon}`} aria-hidden='true' />
        </span>
        <span className='mp-live-tile__title'>دریافت از {source.label}</span>
        <span className='mp-live-pulse' aria-label='در جریان' />
      </div>
      <div className='mp-live-tile__figure is-static'>
        <strong>{formatInvoiceCount(stream.invoices)}</strong>
        <span>صورتحساب در {formatInvoiceCount(minutes)} دقیقه گذشته</span>
      </div>
      <ul className='mp-live-tile__facts'>
        <li>
          {stream.fromTime === stream.toTime
            ? `ساعت ${stream.toTime}`
            : `از ${stream.fromTime} تا ${stream.toTime}`}
          ، آخرین {formatAge(stream.ageSeconds)}
        </li>
      </ul>
    </article>
  )
}

const OperationTile: FC<{
  operation: DashboardLiveOperation
  today?: string
  yesterday?: string
}> = ({operation, today, yesterday}) => {
  const url = getOperationUrl(operation)
  const progress = operation.progressPercent
  const stalled = operation.state === 'stalled'
  const progressText = getOperationProgressText(operation)
  const title = getOperationTitle(operation)

  return (
    <article className={clsx('mp-live-tile', stalled ? 'is-stalled' : 'is-active')}>
      <div className='mp-live-tile__head'>
        <span className={clsx('mp-live-tile__icon', stalled ? 'is-red' : 'is-blue')}>
          <i className={`las ${getOperationIcon(operation)}`} aria-hidden='true' />
        </span>
        <span className='mp-live-tile__title'>{title}</span>
      </div>
      <span className={clsx('mp-live-state', `is-${operation.state}`)}>
        <i
          className={clsx('las', stalled ? 'la-exclamation-circle' : 'la-circle-notch')}
          aria-hidden='true'
        />
        {getOperationStateLabel(operation.state)}
      </span>
      <div
        className={clsx('mp-live-progress', {
          'is-indeterminate': progress === null || progress === undefined,
        })}
        role='progressbar'
        aria-label={`پیشرفت ${title}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress === null || progress === undefined ? undefined : progress}
      >
        <span
          style={progress === null || progress === undefined ? undefined : {width: `${progress}%`}}
        />
      </div>
      <ul className='mp-live-tile__facts'>
        {progressText && <li>{progressText}</li>}
        <li>
          شروع: {formatDayLabel(operation.startedDay, today, yesterday)} ساعت{' '}
          {operation.startedTime}
          {operation.userName ? ` توسط ${operation.userName}` : ''}
        </li>
        {stalled && operation.idleSeconds ? (
          <li>آخرین فعالیت {formatAge(operation.idleSeconds)}</li>
        ) : null}
      </ul>
      {url && (
        <Link className='mp-live-tile__link' to={url}>
          {stalled ? 'بررسی و ادامه' : 'جزئیات'}
          <i className='las la-angle-left' aria-hidden='true' />
        </Link>
      )}
    </article>
  )
}

const DashboardLiveOperations: FC<Props> = ({companyId}) => {
  const {data, isLoading, isError, error, refetch, isFetching} = useDashboardLive(companyId)

  if (isLoading) {
    return (
      <section className='mp-panel mp-live' aria-busy='true'>
        <LiveHeading busy={false} />
        <div className='mp-live__grid'>
          {[0, 1, 2].map((placeholder) => (
            <div key={placeholder} className='mp-live-tile is-loading' aria-hidden='true' />
          ))}
        </div>
      </section>
    )
  }

  if (isError && !data) {
    return (
      <section className='mp-panel mp-live'>
        <LiveHeading busy={false} />
        <div className='mp-dashboard-alert' role='alert'>
          <i className='las la-exclamation-circle' aria-hidden='true' />
          <span>{readDashboardError(error, 'وضعیت عملیات در جریان دریافت نشد.')}</span>
          <button type='button' className='btn btn-sm btn-light-primary' onClick={() => refetch()}>
            تلاش مجدد
          </button>
        </div>
      </section>
    )
  }

  if (!data) {
    return null
  }

  const operations = data.operations || []
  const streams = data.streams || []
  const busy = isLiveSnapshotBusy(data)
  const sending = data.sending || {pending: 0, unknown: 0}

  return (
    <section
      className={clsx('mp-panel mp-live', {'is-refreshing': isFetching})}
      aria-labelledby='mp-live-title'
    >
      <LiveHeading busy={busy} />
      {streams.length > 0 || operations.length > 0 ? (
        <div className='mp-live__grid'>
          <SendQueueTile sending={sending} />
          {streams.map((stream) => (
            <StreamTile
              key={`stream-${stream.entryType ?? 'none'}`}
              stream={stream}
              minutes={data.streamMinutes}
            />
          ))}
          {operations.map((operation) => (
            <OperationTile
              key={operation.key}
              operation={operation}
              today={data.today}
              yesterday={data.yesterday}
            />
          ))}
        </div>
      ) : (
        <SendQueueStrip sending={sending} />
      )}
    </section>
  )
}

const LiveHeading: FC<{busy: boolean}> = ({busy}) => (
  <div className='mp-panel__head'>
    <div>
      <span>به‌روزرسانی خودکار</span>
      <h2 id='mp-live-title'>عملیات در جریان</h2>
    </div>
    <span className={clsx('mp-live-indicator', busy && 'is-busy')}>
      <span className='mp-live-indicator__dot' aria-hidden='true' />
      {busy ? 'هر ۱۰ ثانیه به‌روز می‌شود' : 'بدون عملیات فعال'}
    </span>
  </div>
)

export {DashboardLiveOperations}
